import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ordenesApi, proyectosApi, subproyectosApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { NumberInput } from '../components/NumberInput';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from '../hooks/use-toast';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import ClienteAlegraSelect from '../components/ClienteAlegraSelect';
import { NumeracionCard } from '../components/ordenes-trabajo/NumeracionCard';
import { ClasificacionCard } from '../components/ordenes-trabajo/ClasificacionCard';
import { PresupuestoCard } from '../components/ordenes-trabajo/PresupuestoCard';

const ESTADOS = [
  { value: 'EN EJECUCION', label: 'En Ejecución' },
  { value: 'TERMINADA', label: 'Terminada' },
];

const ESTADOS_FINANCIEROS = [
  { value: 'ACTIVA', label: 'Activa' },
  { value: 'CERRADA', label: 'Cerrada' },
];

const AREAS = [
  { value: 'FABRICACION', label: 'Fabricación' },
  { value: 'MONTAJE', label: 'Montaje' },
  { value: 'OBRAS CIVILES', label: 'Obras Civiles' },
  { value: 'RILES', label: 'Riles' },
  { value: 'ARRIENDO MAQ', label: 'Arriendo Maquinaria' },
  { value: 'TRANSPORTE', label: 'Transporte' },
  { value: 'NO PRODUCTIVO', label: 'No Productivo' },
];

const initialFormData = {
  proyecto_id: '',
  cliente_id: '',
  cliente_nombre: '',
  cliente_alegra_id: '',
  cliente_rut: '',
  contacto_cliente: '',
  descripcion: '',
  responsable: '',
  fecha_inicio: '',
  fecha_termino: '',
  estado: 'EN EJECUCION',
  estado_financiero: 'ACTIVA',
  cotizacion: '',
  orden_compra: '',
  monto: '',
  garantia: false,
  multicliente: false,
  solicitud_trabajo: '',
  observacion: '',
  area: 'FABRICACION',
  avance: 0,
  // Nuevos campos para clasificación
  tipo_ot: 'SUBPROYECTO', // SUBPROYECTO o AUMENTO_OBRA
  subproyecto_id: '', // ID del subproyecto si es AO
  subproyecto_nombre: '', // Nombre descriptivo del subproyecto
  es_aumento_obra: false,
  presupuesto: {
    materiales: 0,
    servicios_terceros: 0,
    valor_hh: 12500,
    numero_hh: 0,
    mano_obra: 0,
    gastos_generales: 0,
    utilidad: 0,
    total: 0,
    kilos: 0,
  },
};

export const OrdenTrabajoForm = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState(initialFormData);
  const [proyectos, setProyectos] = useState([]);
  const [subproyectos, setSubproyectos] = useState([]); // Subproyectos del proyecto seleccionado
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedProyecto, setSelectedProyecto] = useState(null);
  const [_creandoSubproyecto, _setCreandoSubproyecto] = useState(false); // Para crear nuevo subproyecto
  // Numeración (modo crear): N° automático propuesto + visualización de OTs + modo manual
  const [numbering, setNumbering] = useState(null);
  const [numberingLoading, setNumberingLoading] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [numeroManual, setNumeroManual] = useState('');

  // Refs para navegación con Enter
  const inputRefs = {
    materiales: useRef(null),
    servicios_terceros: useRef(null),
    valor_hh: useRef(null),
    numero_hh: useRef(null),
    gastos_generales: useRef(null),
    utilidad: useRef(null),
    kilos: useRef(null),
  };

  const inputOrder = ['materiales', 'servicios_terceros', 'valor_hh', 'numero_hh', 'gastos_generales', 'utilidad', 'kilos'];

  useEffect(() => {
    loadInitialData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadInitialData = async () => {
    try {
      const proyectosRes = await proyectosApi.getAll({ estado: 'VIGENTE' });
      setProyectos(proyectosRes.data);

      if (isEdit) {
        const otRes = await ordenesApi.getOne(id);
        const ot = otRes.data;
        // Normalizar estado (de Access puede venir COMPLETADA)
        let estado = ot.estado;
        if (estado === 'COMPLETADA' || estado === 'CERRADA' || estado === 'PENDIENTE' || estado === 'DETENIDA') {
          estado = 'TERMINADA';
        }
        // Determinar estado financiero desde estfinan
        let estadoFinanciero = 'ACTIVA';
        if (ot.estfinan === false || ot.estfinan === 'NO') {
          estadoFinanciero = 'CERRADA';
        }
        // Si está en ejecución, siempre activa
        if (estado === 'EN EJECUCION') {
          estadoFinanciero = 'ACTIVA';
        }
        
        const proyecto = proyectosRes.data.find(p => p.id === ot.proyecto_id);

        // Iter 232.7 — si la OT no tiene cliente Alegra pero el proyecto sí, usar el del proyecto.
        const inherited = {};
        if (!ot.cliente_alegra_id && proyecto?.cliente_alegra_id) {
          inherited.cliente_alegra_id = proyecto.cliente_alegra_id;
          inherited.cliente_nombre = proyecto.cliente_nombre || ot.cliente_nombre || '';
          inherited.cliente_rut = proyecto.cliente_rut || '';
        }

        setFormData({
          ...ot,
          estado: estado,
          estado_financiero: estadoFinanciero,
          cotizacion: ot.cotizacion || '',
          orden_compra: ot.orden_compra || '',
          monto: ot.monto || '',
          tipo_ot: ot.tipo_ot || 'SUBPROYECTO',
          subproyecto_id: ot.subproyecto_id || '',
          subproyecto_nombre: ot.subproyecto_nombre || '',
          cliente_alegra_id: ot.cliente_alegra_id || '',
          cliente_nombre: ot.cliente_nombre || '',
          cliente_rut: ot.cliente_rut || '',
          ...inherited,
          presupuesto: {
            ...initialFormData.presupuesto,
            ...ot.presupuesto,
          },
        });
        if (proyecto) {
          setSelectedProyecto(proyecto);
          // Cargar subproyectos del proyecto
          loadSubproyectos(proyecto.codigo);
        }
      }
    } catch (error) {

      toast({ title: 'Error al cargar los datos', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const updates = { ...prev, [field]: value };
      
      // Regla: Si está EN EJECUCION, siempre está ACTIVA
      if (field === 'estado' && value === 'EN EJECUCION') {
        updates.estado_financiero = 'ACTIVA';
      }
      
      // Si cambia tipo_ot, actualizar es_aumento_obra
      if (field === 'tipo_ot') {
        updates.es_aumento_obra = value === 'AUMENTO_OBRA';
        if (value === 'SUBPROYECTO') {
          updates.subproyecto_id = '';
        }
      }
      
      return updates;
    });

    if (field === 'proyecto_id' && value) {
      const proyecto = proyectos.find((p) => p.id === value);
      if (proyecto) {
        setSelectedProyecto(proyecto);
        // Iter 232.7 (Carlos): heredar el cliente del proyecto como DEFAULT.
        // Editable: en OTs como transporte se cambia por cliente final no habitual.
        setFormData((prev) => ({
          ...prev,
          proyecto_id: value,
          cliente_id: proyecto.cliente_id,
          cliente_alegra_id: proyecto.cliente_alegra_id || prev.cliente_alegra_id || '',
          cliente_nombre: proyecto.cliente_nombre || prev.cliente_nombre || '',
          cliente_rut: proyecto.cliente_rut || prev.cliente_rut || '',
          subproyecto_id: '', // Reset subproyecto al cambiar proyecto
        }));
        // Cargar subproyectos del proyecto
        loadSubproyectos(proyecto.codigo);
        if (!isEdit) {
          setManualMode(false);
          setNumeroManual('');
          loadNumbering(proyecto.codigo);
        }
      }
    }
  };

  // Cargar estado de numeración (N° automático propuesto + OTs existentes + huecos)
  const loadNumbering = async (proyectoCodigo) => {
    if (!proyectoCodigo) { setNumbering(null); return; }
    setNumberingLoading(true);
    try {
      const res = await ordenesApi.numbering(proyectoCodigo);
      setNumbering(res.data);
    } catch (err) {
      setNumbering(null);
    } finally {
      setNumberingLoading(false);
    }
  };

  // Cargar subproyectos existentes de un proyecto
  const loadSubproyectos = async (proyectoCodigo) => {
    try {
      const res = await subproyectosApi.getByProyecto(proyectoCodigo);
      setSubproyectos(res.data || []);
    } catch (err) {
      // 403/404 esperables (sin subproyectos o sin permiso): no es bloqueante.
      setSubproyectos([]);
    }
  };

  const handlePresupuestoChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    setFormData((prev) => {
      const newPresupuesto = { ...prev.presupuesto, [field]: numValue };
      newPresupuesto.mano_obra = newPresupuesto.valor_hh * newPresupuesto.numero_hh;
      newPresupuesto.total = 
        newPresupuesto.materiales + 
        newPresupuesto.servicios_terceros + 
        newPresupuesto.mano_obra +
        newPresupuesto.gastos_generales +
        newPresupuesto.utilidad;
      return { ...prev, presupuesto: newPresupuesto };
    });
  };

  const handleKeyDown = (e, currentField) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const currentIndex = inputOrder.indexOf(currentField);
      const nextIndex = currentIndex + 1;
      if (nextIndex < inputOrder.length) {
        const nextField = inputOrder[nextIndex];
        inputRefs[nextField]?.current?.focus();
        inputRefs[nextField]?.current?.select();
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.proyecto_id || !formData.descripcion) {
      toast({ title: 'Complete los campos obligatorios: Proyecto y Descripción', variant: 'destructive' });
      return;
    }

    // Solo advertencia si monto no coincide con presupuesto (no bloquea)
    const monto = parseFloat(formData.monto) || 0;
    const totalPresupuesto = formData.presupuesto.total;
    
    if (monto > 0 && totalPresupuesto > 0 && monto !== totalPresupuesto) {
      const diff = Math.abs(monto - totalPresupuesto);

    }

    setSaving(true);
    try {
      const dataToSend = {
        proyecto_id: formData.proyecto_id,
        cliente_id: formData.cliente_id,
        cliente_nombre: formData.cliente_nombre || selectedProyecto?.cliente_nombre || '',
        // Iter 232.7 (Carlos) — Alegra fields para downstream proforma/factura.
        cliente_alegra_id: formData.cliente_alegra_id || selectedProyecto?.cliente_alegra_id || '',
        cliente_rut: formData.cliente_rut || selectedProyecto?.cliente_rut || '',
        contacto_cliente: formData.contacto_cliente || null,
        descripcion: formData.descripcion,
        responsable: formData.responsable || null,
        fecha_inicio: formData.fecha_inicio || null,
        fecha_termino: formData.fecha_termino || null,
        estado: formData.estado,
        cotizacion: formData.cotizacion || null,
        orden_compra: formData.orden_compra || null,
        monto: formData.monto ? parseFloat(formData.monto) : 0,
        garantia: formData.garantia || false,
        multicliente: !!formData.multicliente,
        solicitud_trabajo: formData.solicitud_trabajo || null,
        observacion: formData.observacion || null,
        area: formData.area,
        avance: formData.avance !== '' && formData.avance != null ? parseFloat(formData.avance) : 0,
        presupuesto: formData.presupuesto,
        es_aumento_obra: formData.es_aumento_obra || false,
        estfinan: formData.estado_financiero === 'ACTIVA',
        tipo_ot: formData.tipo_ot || 'SUBPROYECTO',
        subproyecto_id: formData.subproyecto_id || null,
      };

      if (isEdit) {
        await ordenesApi.update(id, dataToSend);
        toast({ title: 'Orden de trabajo actualizada' });
      } else {
        if (manualMode && numeroManual) {
          dataToSend.numero_ot = parseInt(numeroManual, 10);
        }
        const res = await ordenesApi.create(dataToSend);
        toast({ title: `Orden de trabajo creada: OT-${res.data?.numero_ot ?? ''}` });
      }
      navigate('/ordenes-trabajo');
    } catch (error) {

      toast({ title: error.response?.data?.detail || 'Error al guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const monto = parseFloat(formData.monto) || 0;
  const totalPresupuesto = formData.presupuesto.total;
  const presupuestoMismatch = monto > 0 && totalPresupuesto > 0 && monto !== totalPresupuesto;
  const manualOcupado = manualMode && !!numeroManual && (numbering?.usados || []).some((u) => String(u.numero) === String(numeroManual));

  if (loading) {
    return (<div className="space-y-6"><Skeleton className="h-10 w-64" /><Skeleton className="h-[500px]" /></div>);
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl" data-testid="orden-trabajo-form">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">
            {isEdit ? `Editar OT-${formData.numero_ot}` : 'Nueva Orden de Trabajo'}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader className="py-4">
            <CardTitle className="font-heading text-lg">Datos de la OT</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Proyecto *</Label>
                <select
                  value={formData.proyecto_id}
                  onChange={(e) => handleChange('proyecto_id', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="select-proyecto"
                >
                  <option value="">Seleccione proyecto</option>
                  {proyectos.map((proyecto) => (
                    <option key={proyecto.id} value={proyecto.id}>
                      {proyecto.codigo} - {proyecto.descripcion?.substring(0, 40)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label>Cliente</Label>
                <ClienteAlegraSelect
                  value={(formData.cliente_alegra_id || formData.cliente_id) ? { alegra_id: formData.cliente_alegra_id, cliente_id: formData.cliente_id, nombre: formData.cliente_nombre, rut: formData.cliente_rut } : null}
                  onChange={(sel) => setFormData((d) => ({
                    ...d,
                    cliente_alegra_id: sel?.alegra_id || '',
                    cliente_id: sel?.cliente_id || d.cliente_id || '',
                    cliente_nombre: sel?.nombre || '',
                    cliente_rut: sel?.rut || '',
                  }))}
                  placeholder="Cliente del proyecto (puedes cambiarlo si la OT es de transporte u otro cliente final)"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Descripción *</Label>
              <Textarea
                value={formData.descripcion}
                onChange={(e) => handleChange('descripcion', e.target.value)}
                placeholder="Descripción de la orden de trabajo"
                rows={2}
                data-testid="input-descripcion"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Estado</Label>
                <select
                  value={formData.estado}
                  onChange={(e) => handleChange('estado', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="select-estado"
                >
                  {ESTADOS.map((e) => (
                    <option key={e.value} value={e.value}>{e.label}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label>Estado Financiero</Label>
                <select
                  value={formData.estado_financiero}
                  onChange={(e) => handleChange('estado_financiero', e.target.value)}
                  disabled={formData.estado === 'EN EJECUCION'}
                  className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring ${formData.estado === 'EN EJECUCION' ? 'bg-muted cursor-not-allowed' : ''}`}
                  data-testid="select-estado-financiero"
                >
                  {ESTADOS_FINANCIEROS.map((e) => (
                    <option key={e.value} value={e.value}>{e.label}</option>
                  ))}
                </select>
                {formData.estado === 'EN EJECUCION' && (
                  <p className="text-xs text-muted-foreground">OT en ejecución siempre está Activa</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Área</Label>
                <select
                  value={formData.area}
                  onChange={(e) => handleChange('area', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="select-area"
                >
                  {AREAS.map((a) => (
                    <option key={a.value} value={a.value}>{a.label}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label>Responsable</Label>
                <Input
                  value={formData.responsable}
                  onChange={(e) => handleChange('responsable', e.target.value)}
                  placeholder="Nombre responsable"
                  data-testid="input-responsable"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Fecha Inicio</Label>
                <Input type="date" value={formData.fecha_inicio} onChange={(e) => handleChange('fecha_inicio', e.target.value)} data-testid="input-fecha-inicio" />
              </div>
              <div className="space-y-2">
                <Label>Fecha Término</Label>
                <Input type="date" value={formData.fecha_termino} onChange={(e) => handleChange('fecha_termino', e.target.value)} data-testid="input-fecha-termino" />
              </div>
              <div className="space-y-2">
                <Label>Cotización</Label>
                <Input type="number" value={formData.cotizacion} onChange={(e) => handleChange('cotizacion', e.target.value)} placeholder="N°" data-testid="input-cotizacion" />
              </div>
              <div className="space-y-2">
                <Label>Orden Compra</Label>
                <Input type="number" value={formData.orden_compra} onChange={(e) => handleChange('orden_compra', e.target.value)} placeholder="N°" data-testid="input-oc" />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Monto ($)</Label>
                <NumberInput allowDecimal={false} allowNegative={false} value={formData.monto} onChange={(clean) => handleChange('monto', clean)} placeholder="0" data-testid="input-monto" />
              </div>
              <div className="space-y-2">
                <Label>Avance (%)</Label>
                <Input type="number" min="0" max="100" value={formData.avance} onChange={(e) => handleChange('avance', e.target.value)} placeholder="0" data-testid="input-avance" />
              </div>
              <div className="space-y-2">
                <Label>Solicitud Trabajo</Label>
                <Input value={formData.solicitud_trabajo} onChange={(e) => handleChange('solicitud_trabajo', e.target.value)} placeholder="N° ST" data-testid="input-st" />
              </div>
              <div className="space-y-2">
                <Label>Contacto Cliente</Label>
                <Input value={formData.contacto_cliente} onChange={(e) => handleChange('contacto_cliente', e.target.value)} placeholder="Nombre contacto" data-testid="input-contacto" />
              </div>
              <div className="flex items-end pb-2">
                <div className="flex items-center space-x-2">
                  <Checkbox id="garantia" checked={formData.garantia} onCheckedChange={(checked) => handleChange('garantia', checked)} data-testid="checkbox-garantia" />
                  <Label htmlFor="garantia" className="cursor-pointer">Garantía</Label>
                </div>
              </div>
              <div className="flex items-end pb-2 md:col-span-2">
                <div className="flex items-start space-x-2">
                  <Checkbox id="multicliente" checked={!!formData.multicliente} onCheckedChange={(checked) => handleChange('multicliente', !!checked)} data-testid="checkbox-multicliente" className="mt-0.5" />
                  <div>
                    <Label htmlFor="multicliente" className="cursor-pointer">OT centralizadora (multi-cliente)</Label>
                    <p className="text-xs text-muted-foreground" data-testid="multicliente-hint">
                      Marca explícita (no se infiere por el cliente). Transporte, vehículos o servicios recurrentes: la OT centraliza gastos e ingresos de varios clientes. El cliente de la OT
                      es solo referencia interna (puede ser CHS); cada proforma indica al cliente real facturado y no se alerta por la diferencia.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* NUMERACIÓN: N° automático + visualización de OTs existentes + modo manual */}
        {!isEdit && formData.proyecto_id && (
          <NumeracionCard
            numbering={numbering}
            numberingLoading={numberingLoading}
            manualMode={manualMode}
            setManualMode={setManualMode}
            numeroManual={numeroManual}
            setNumeroManual={setNumeroManual}
            manualOcupado={manualOcupado}
          />
        )}

        {/* NUEVA SECCIÓN: Clasificación Subproyecto / Aumento de Obra */}
        <ClasificacionCard formData={formData} handleChange={handleChange} subproyectos={subproyectos} />

        <PresupuestoCard
          presupuesto={formData.presupuesto}
          handlePresupuestoChange={handlePresupuestoChange}
          handleKeyDown={handleKeyDown}
          inputRefs={inputRefs}
          presupuestoMismatch={presupuestoMismatch}
          monto={monto}
          totalPresupuesto={totalPresupuesto}
        />

        <Card>
          <CardHeader className="py-4"><CardTitle className="font-heading text-lg">Observaciones</CardTitle></CardHeader>
          <CardContent>
            <Textarea value={formData.observacion} onChange={(e) => handleChange('observacion', e.target.value)} placeholder="Observaciones adicionales..." rows={3} data-testid="input-observacion" />
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="cancel-btn">Cancelar</Button>
          <Button type="submit" disabled={saving || manualOcupado} data-testid="save-btn">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Save className="w-4 h-4 mr-2" />
            {isEdit ? 'Guardar' : 'Crear OT'}
          </Button>
        </div>
      </form>
    </div>
  );
};
